/** @jsxImportSource @emotion/react */

import { ChangeEvent, FC, useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import Pagination from '@mui/material/Pagination'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import { reduceRight } from 'lodash'
import { FIND_ALL_USER_PRACTICE_TEST_STATS } from 'queries/users'
import Table, { Order } from 'components/Table'
import { CourseName } from 'theme/utils'
import { useLink } from 'routes/cmsRoutes'
import { OrderDirection, UserStatsPracticeTestOrder } from '__generatedTypes__/globalTypes'
import {
  findAllUserPracticeTestStats,
  findAllUserPracticeTestStatsVariables,
  findAllUserPracticeTestStats_findAllUserPracticeTestStats_items as Item,
} from '__generatedTypes__/findAllUserPracticeTestStats'
import { useAuthContext } from 'utils/auth'
import * as classes from './styles'

const secondsToDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  const formattedMinutes = String(minutes)
  const formattedSeconds = String(remainingSeconds).padStart(2, '0')
  return `${formattedMinutes}m ${formattedSeconds}s`
}

const TAKE = 10

const REACT_APP_NEXT_APP_ORIGIN = process.env.REACT_APP_NEXT_APP_ORIGIN || ''

export const INITIAL_SORT = {
  field: 'createdAt',
  direction: OrderDirection.DESC,
}

export const WEB_APP_ORIGIN = REACT_APP_NEXT_APP_ORIGIN.endsWith('/')
  ? REACT_APP_NEXT_APP_ORIGIN.slice(0, -1)
  : REACT_APP_NEXT_APP_ORIGIN
const WEB_PT_DETAILS_ADMIN_RESULT = 'admin-preview/:userId/practice-tests/:practiceTestId/result/:attemptId'

export const PracticeTestDetails: FC = () => {
  const { isAdmin } = useAuthContext()
  const { getLink } = useLink()
  const [skip, setSkip] = useState(0)
  const { userId = '', platformId = '' } = useParams()
  const [order, setOrder] = useState<Order>(INITIAL_SORT)
  const [items, setItems] = useState<Item[]>([])
  const [count, setCount] = useState(0)

  const [, { variables, refetch }] = useLazyQuery<findAllUserPracticeTestStats, findAllUserPracticeTestStatsVariables>(
    FIND_ALL_USER_PRACTICE_TEST_STATS,
    {
      variables: {
        query: {
          skip,
          take: TAKE,
          order: {
            [INITIAL_SORT.field]: INITIAL_SORT.direction,
          },
          where: {
            user: {
              id: userId,
            },
            practiceTest: {
              subject: {
                platform: {
                  id: platformId,
                },
              },
            },
          },
        },
      },
    }
  )

  const getTableData = async (variables?: findAllUserPracticeTestStatsVariables) => {
    try {
      const { data } = await refetch(variables)

      const ptProgress = data?.findAllUserPracticeTestStats
      setItems(ptProgress?.items ?? [])
      setCount(ptProgress?.count || 0)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    getTableData(variables)
  }, [])

  const handlePaginationChange = (_event: ChangeEvent<unknown>, page: number) => {
    const newSkip = (page - 1) * TAKE
    if (newSkip !== skip) {
      setSkip(newSkip)

      getTableData({
        query: {
          ...variables?.query,
          skip: newSkip,
        },
      })
    }
  }

  const handleSortModelChange = async (sortOrder: Order) => {
    const { field, direction: orderModel } = sortOrder || {}

    setOrder(sortOrder)

    if (field && orderModel) {
      const sortField = field as keyof UserStatsPracticeTestOrder

      const keys = field.split('.')

      const nestedSort = reduceRight<string, UserStatsPracticeTestOrder>(
        keys,
        (acc, key) => ({ [key]: acc }),
        orderModel as unknown as UserStatsPracticeTestOrder
      )

      const isNestedOrder = keys.length > 1

      const order = isNestedOrder
        ? nestedSort
        : {
            [sortField]: orderModel,
          }

      getTableData({
        query: {
          ...variables?.query,
          order,
        },
      })
    }
  }

  return (
    <section css={classes.container}>
      <Typography variant="title3" css={classes.tableTitle}>
        Practice Test Details
      </Typography>
      <div css={classes.tableWrapper}>
        <Table
          css={classes.table}
          columns={[
            {
              title: 'Test',
              renderCell: (practiceTestStat) => (
                <div css={classes.practiceTestNameCell}>{practiceTestStat.practiceTest.title}</div>
              ),
              orderField: 'practiceTest.title',
              flex: 2,
            },
            {
              title: 'Topic',
              renderCell: (practiceTestStat) => (
                <div css={classes.topic(practiceTestStat.practiceTest.subject.tabName as CourseName)}>
                  {practiceTestStat.practiceTest.subject.tabName}
                </div>
              ),
              orderField: 'practiceTest.subject.tabName',
              flex: 1,
            },
            {
              title: 'Attempt',
              renderCell: (practiceTestStat) => <div>{practiceTestStat.attemptNumber}</div>,
              orderField: 'attemptNumber',
              flex: 1,
            },
            {
              title: 'Score',
              renderCell: (practiceTestStat) => (
                <div>
                  {practiceTestStat.answeredCorrectlyCount}/{practiceTestStat.totalQuestionsCount}
                </div>
              ),
              orderField: 'answeredCorrectlyCount',
              flex: 1,
            },
            {
              title: 'Time Spent',
              renderCell: (practiceTestStat) => <div>{secondsToDuration(practiceTestStat.totalTimeSpentCount)}</div>,
              orderField: 'totalTimeSpentCount',
              flex: 1,
            },
            {
              title: 'Date',
              renderCell: (practiceTestStat) => <div>{format(new Date(practiceTestStat.createdAt), 'MM/dd/yyyy')}</div>,
              orderField: 'createdAt',
              flex: 1,
            },
          ]}
          items={items}
          onOrder={handleSortModelChange}
          order={order}
          onItemClick={({ id, practiceTest }) => {
            if (!isAdmin) {
              return
            }

            window.open(
              `${WEB_APP_ORIGIN}/${getLink(WEB_PT_DETAILS_ADMIN_RESULT, {
                userId,
                practiceTestId: practiceTest.id,
                attemptId: id,
              })}`,
              '_blank'
            )
          }}
        />

        <Pagination count={Math.ceil(count / TAKE)} onChange={handlePaginationChange} css={classes.pagination} />
      </div>
    </section>
  )
}
